if (!customElements.get("f-cart-goal")) {
  class FCartGoal extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartDrawer: ".f-cart-drawer__inner",
        message:    "[data-goal-message]",
        fill:       "[data-goal-fill]",
        milestones: "[data-milestone]",
      };

      this.money_format  = window.FoxThemeSettings.money_format;
      this.enableConfetti = this.dataset.enableConfetti === "true";
      this.confettiShown  = false;

      // Currency rate for multi-currency stores (goals are stored in shop base currency)
      this.currencyRate = Number(window.Shopify?.currency?.rate || 1);

      // Reward 1 — free shipping (amounts in shop-currency units, NOT cents)
      this.reward1Goal        = Number(this.dataset.reward1Goal || 0) * this.currencyRate;
      this.reward1MessageTpl  = this.dataset.reward1Message || "";
      this.reward1SuccessMsg  = this.dataset.reward1Success || "";

      // Reward 1 — auto-managed gift product (added above threshold, removed below)
      this.reward1VariantId   = this.dataset.reward1VariantId || "";
      this.reward1GiftInCart  = this.dataset.reward1GiftInCart === "true";
      this.giftPropertyKey    = "_gift_reward1";
      this._syncBusy          = false;

      // Reward 2 — second milestone
      this.reward2Enabled     = this.dataset.reward2Enabled === "true";
      this.reward2Goal        = Number(this.dataset.reward2Goal || 0) * this.currencyRate;
      this.reward2MessageTpl  = this.dataset.reward2Message || "";
      this.reward2SuccessMsg  = this.dataset.reward2Success || "";
    }

    connectedCallback() {
      this.fillEl        = this.querySelector(this.selectors.fill);
      this.messageEl     = this.querySelector(this.selectors.message);
      this.milestoneEls  = [...this.querySelectorAll(this.selectors.milestones)];

      // Initial render: the server already excluded the gift line price from
      // data-cart-total, so this value is the "real" subtotal we compare to
      // the Reward 1 threshold.
      this.updateGoal(Number(this.dataset.cartTotal));

      // Server-side hint is enough to schedule an initial sync (may add or remove
      // the gift if the cart state is inconsistent on first load).
      this._syncGiftToCart(null);

      // Re-sync on every cart update
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
        const newCart = await window.FoxThemeCartHelpers.getState();
        if (!newCart) return;
        this._syncGiftStateFromCart(newCart);
        const realSubtotal = this._getRealSubtotal(newCart);
        this.updateGoal(realSubtotal);
        this._syncGiftToCart(newCart);
      });

      this.confettiShown = this._allDone(this.reward1Goal * 100);
    }

    /**
     * "Real" cart subtotal in cents: items_subtotal_price minus the gift line
     * item's final_line_price. Used everywhere we compare the cart to the
     * Reward 1 threshold so the gift never lifts the cart above its own goal.
     */
    _getRealSubtotal(cart) {
      if (!cart) return 0;
      const subtotal = Number(cart.items_subtotal_price) || 0;
      const giftLine = this._findGiftLineItem(cart);
      const giftValue = giftLine ? Number(giftLine.final_line_price) || 0 : 0;
      return Math.max(0, subtotal - giftValue);
    }

    // totalInCents — value from Shopify cart API (smallest currency unit)
    updateGoal(totalInCents) {
      const cartAmount = totalInCents / 100; // convert to currency units

      // Show / hide the whole component
      if (totalInCents > 0) {
        this.classList.remove("hidden");
      } else {
        this.classList.add("hidden");
        return;
      }

      const reward1Done = cartAmount >= this.reward1Goal;
      const reward2Done = this.reward2Enabled && cartAmount >= this.reward2Goal;

      // Piecewise progress so the fill bar reaches each milestone circle exactly
      // when the cart total hits that threshold.
      // • Single milestone: linear 0 → 100%
      // • Two milestones:   0 → 50% for reward1, then 50 → 100% for reward2
      //   (milestones are always evenly distributed visually)
      let percent;
      if (this.reward2Enabled) {
        if (cartAmount <= this.reward1Goal) {
          percent = (cartAmount / this.reward1Goal) * 50;
        } else if (cartAmount <= this.reward2Goal) {
          percent = 50 + ((cartAmount - this.reward1Goal) / (this.reward2Goal - this.reward1Goal)) * 50;
        } else {
          percent = 100;
        }
      } else {
        percent = Math.min((cartAmount / this.reward1Goal) * 100, 100);
      }
      this.style.setProperty("--progress", `${percent}%`);
      if (this.fillEl) this.fillEl.style.width = `${percent}%`;

      // Milestone states
      this.milestoneEls.forEach((el) => {
        const n = el.dataset.milestone;
        const done = n === "1" ? reward1Done : n === "2" ? reward2Done : false;
        el.classList.toggle("f-cartgoal__milestone--done", done);
      });

      // Message
      this._updateMessage(totalInCents, reward1Done, reward2Done);

      // Confetti when the LAST active milestone is reached
      if (this._allDone(totalInCents) && !this.confettiShown && this.enableConfetti) {
        this._showConfetti();
      }
    }

    _allDone(totalInCents) {
      if (this.reward2Enabled) return totalInCents / 100 >= this.reward2Goal;
      return totalInCents / 100 >= this.reward1Goal;
    }

    _updateMessage(totalInCents, reward1Done, reward2Done) {
      if (!this.messageEl) return;

      let html = "";

      if (reward2Done) {
        // All rewards unlocked
        html = this.reward2SuccessMsg || this.reward1SuccessMsg;
        this.classList.add("f-cartgoal--done");

      } else if (reward1Done && this.reward2Enabled) {
        // Shipping unlocked — show progress toward reward 2
        const remaining = Math.max(0, this.reward2Goal * 100 - totalInCents);
        const amountHtml = `<span data-left-to-spend>${formatMoney(remaining, this.money_format)}</span>`;
        html = (this.reward2MessageTpl || "Mancano solo {{amount}} per il tuo omaggio")
          .replace("{{amount}}", amountHtml);
        this.classList.remove("f-cartgoal--done");

      } else if (reward1Done) {
        // Only one milestone and it's done
        html = this.reward1SuccessMsg;
        this.classList.add("f-cartgoal--done");

      } else {
        // Still working toward reward 1
        const remaining = Math.max(0, this.reward1Goal * 100 - totalInCents);
        const amountHtml = `<span data-left-to-spend>${formatMoney(remaining, this.money_format)}</span>`;
        html = (this.reward1MessageTpl || "Spend {{amount}} more for free shipping")
          .replace("{{amount}}", amountHtml);
        this.classList.remove("f-cartgoal--done");
      }

      this.messageEl.innerHTML = html;
    }

    // ------------------------------------------------------------------
    // Reward 1 — auto-managed gift product
    // ------------------------------------------------------------------

    /**
     * Sync the locally cached "is gift in cart?" flag and remember the line
     * item key so we can target it precisely when we need to remove it.
     */
    _syncGiftStateFromCart(cart) {
      if (!cart) return;
      const giftLine = this._findGiftLineItem(cart);
      this.reward1GiftInCart = !!giftLine;
      this.reward1GiftKey    = giftLine ? giftLine.key : null;
    }

    /**
     * The "gift" line item is the one we marked with the dedicated property.
     * We never identify by variant_id alone, so a manual purchase of the same
     * product by the customer is never touched by this auto-management.
     */
    _findGiftLineItem(cart) {
      if (!cart || !Array.isArray(cart.items)) return null;
      return cart.items.find(
        (it) => it.properties && it.properties[this.giftPropertyKey]
      ) || null;
    }

    /**
     * Decide whether to add or remove the gift, based on the REAL cart
     * subtotal (excluding the gift line itself) vs the Reward 1 threshold.
     * Idempotent: safe to call repeatedly.
     */
    async _syncGiftToCart(cart) {
      if (!this.reward1VariantId) return;
      if (this._syncBusy) return;

      const realSubtotalCents = cart
        ? this._getRealSubtotal(cart)
        : Number(this.dataset.cartTotal);
      const cartAmount  = realSubtotalCents / 100;
      const aboveLimit  = cartAmount >= this.reward1Goal;
      const inCart      = this.reward1GiftInCart;

      if (aboveLimit && inCart) return;
      if (!aboveLimit && !inCart) return;
      if (aboveLimit && realSubtotalCents <= 0) return;

      this._syncBusy = true;
      try {
        if (aboveLimit && !inCart) {
          await this._addGiftLineItem();
        } else if (!aboveLimit && inCart) {
          await this._removeGiftLineItem();
        }
        await this._refreshCartUi();
      } catch (err) {
        console.error("[cart-goal] gift auto-sync failed", err);
      } finally {
        this._syncBusy = false;
      }
    }

    async _addGiftLineItem() {
      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
      });
      const body = {
        items: [
          {
            id: Number(this.reward1VariantId),
            quantity: 1,
            properties: { [this.giftPropertyKey]: "yes" },
          },
        ],
      };
      const res = await fetch(window.FoxThemeSettings.routes.cart_add_url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`cart/add failed: ${res.status} ${text}`);
      }
      this.reward1GiftInCart = true;
      return res.json();
    }

    async _removeGiftLineItem() {
      if (!this.reward1GiftKey) {
        // Fallback: try to refetch and locate the gift line.
        const cart = await window.FoxThemeCartHelpers.getState();
        this._syncGiftStateFromCart(cart);
        if (!this.reward1GiftKey) return;
      }
      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
      });
      const res = await fetch(window.FoxThemeSettings.routes.cart_change_url, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: this.reward1GiftKey, quantity: 0 }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`cart/change failed: ${res.status} ${text}`);
      }
      this.reward1GiftInCart = false;
      this.reward1GiftKey    = null;
      return res.json();
    }

    /**
     * After mutating the cart, refresh visible cart UIs (drawer / cart page)
     * and let other listeners (cart count, recommendations, etc.) update.
     */
    async _refreshCartUi() {
      try {
        const cart = await window.FoxThemeCartHelpers.getState();
        if (!cart) return;

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
          try {
            const sections = cartDrawer
              .getSectionsToRender()
              .map((s) => s.id)
              .filter((v, i, a) => a.indexOf(v) === i);

            const sectionsRes = await fetch(window.FoxThemeSettings.routes.cart_update_url, {
              method: "POST",
              headers: new Headers({
                "Content-Type": "application/json",
                Accept: "application/json",
              }),
              body: JSON.stringify({
                sections,
                sections_url: window.location.pathname,
              }),
            });
            const withSections = await sectionsRes.json();
            cart.sections = withSections.sections;
          } catch (e) {
            /* best-effort: the drawer falls back to a soft refresh */
          }
        }

        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, cart);

        // The standalone cart page doesn't subscribe to cartUpdate, so request
        // a hard refresh of its sections.
        if (this.closest(".cart-template")) {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }
      } catch (e) {
        console.error("[cart-goal] refresh after gift sync failed", e);
      }
    }

    _showConfetti() {
      const cartDrawer   = this.closest(this.selectors.cartDrawer);
      const cartTemplate = this.closest(".cart-template");

      if (cartDrawer || cartTemplate) {
        setTimeout(() => {
          if (cartDrawer)   new window.FoxTheme.Confetti(cartDrawer);
          if (cartTemplate) new window.FoxTheme.Confetti(document.body);
          this.confettiShown = true;
        }, 800);
      }
    }
  }

  customElements.define("f-cart-goal", FCartGoal);
}
